/* ============================================
   TEMPLATE SELECTOR — Selection + cooldown + interpolation
   ============================================
   Charge les templates depuis catalog-store.js,
   filtre par transition fromZone -> toZone,
   applique le cooldown par ID,
   selection aleatoire ponderee,
   interpole les variables.
   ============================================ */

import { getTemplatesForTransition, loadTemplates, getTemplatesForEvent, loadEventTemplates } from '../catalog/catalog-store.js'
import { state } from '../state.js'
import { flankToSide, zoneToLabel } from './zone-resolver.js'
import { getChronoData } from '../ui/radar-chrono.js'

const HISTORY_SIZE = 15
const DEFAULT_COOLDOWN = 3

export class TemplateSelector {
  constructor(options = {}) {
    this.lang = options.lang || 'fr'
    this.loaded = false
    this.onLog = options.onLog || ((msg) => console.log(msg))

    // Anti-repetition state
    this.history = []       // last N template IDs played
    this.lastTemplateId = null  // prevent immediate repeat
  }

  /**
   * Load templates from the API (must be called once before select()).
   */
  async load(lang) {
    if (lang) this.lang = lang
    await Promise.all([loadTemplates(this.lang), loadEventTemplates(this.lang)])
    this.loaded = true
    this.onLog(`[TEMPLATE-SELECTOR] Templates loaded (lang=${this.lang})`)
  }

  /**
   * Select a template for the given transition.
   *
   * @param {string|null} fromZone - Previous zone (null if first touch)
   * @param {string} toZone - Current zone
   * @param {string|null} flank - 'left', 'right', or null
   * @param {object} vars - Variables for interpolation
   * @returns {string|null} Interpolated text, or null if no template found
   */
  select(fromZone, toZone, flank, vars = {}, opts = {}) {
    if (!this.loaded) {
      this.onLog('[TEMPLATE-SELECTOR] Not loaded yet — skipping')
      return null
    }

    // 1. Get candidate templates for this transition
    let pool = getTemplatesForTransition(fromZone || '*', toZone)

    // 2. Filter by flank if the template specifies one
    if (flank) {
      const flanked = pool.filter(t => !t.flank || t.flank === flank || t.flank === 'any')
      if (flanked.length > 0) pool = flanked
    }

    // 3. Fallback: try wildcard templates
    if (pool.length === 0) {
      pool = getTemplatesForTransition('*', toZone)
    }
    if (pool.length === 0) {
      pool = getTemplatesForTransition('*', '*')
    }

    // 3b. Recovery: if this is a post-turnover touch, try recovery-tagged templates first
    if (opts.recovery && pool.length > 0) {
      const recoveryPool = pool.filter(t => t.tags && t.tags.includes('recovery'))
      if (recoveryPool.length > 0) {
        pool = recoveryPool
        this.onLog(`[TEMPLATE-SELECTOR] Recovery mode — ${pool.length} recovery templates`)
      }
    }

    // 3c. Ball carry: prefer dribble-tagged templates when same player twice
    if (vars.ballCarry && pool.length > 0) {
      const dribblePool = pool.filter(t => t.tags && t.tags.includes('dribble'))
      if (dribblePool.length > 0) {
        pool = dribblePool
        this.onLog(`[TEMPLATE-SELECTOR] Ball carry — ${pool.length} dribble templates`)
      }
    }

    // 3d. If prevPlayer is null (same player dedup), prefer templates without {prevPlayer}
    if (!vars.prevPlayer && pool.length > 0) {
      const noPrev = pool.filter(t => !t.text.includes('{prevPlayer}'))
      if (noPrev.length > 0) pool = noPrev
    }

    // 4. No template found at all — return null so the engine can fallback to LLM
    if (pool.length === 0) {
      return null
    }

    // 5. Exclude templates in cooldown
    const cooldownSet = new Set(this.history)
    let filtered = pool.filter(t => !cooldownSet.has(t.id))

    // 6. Exclude last template played (no immediate repeat)
    if (this.lastTemplateId) {
      filtered = filtered.filter(t => t.id !== this.lastTemplateId)
    }

    // If all filtered out, relax: use full pool minus just the last one
    if (filtered.length === 0) {
      filtered = pool.filter(t => t.id !== this.lastTemplateId)
    }
    if (filtered.length === 0) {
      filtered = pool
    }

    // 7. Situation tags — boost matching templates
    const situationTags = this._getSituationTags(vars.team)
    if (situationTags.length > 0) {
      const tagged = filtered.filter(t =>
        t.tags && t.tags.some(tag => situationTags.includes(tag))
      )
      if (tagged.length > 0) {
        // Boost tagged templates by adding them again (effectively weight x2)
        filtered = [...filtered, ...tagged]
      }
    }

    // 8. Weighted random selection
    const selected = this._weightedRandom(filtered)

    // 9. Record in history
    this._recordUsage(selected.id, selected.cooldown)

    // 10. Interpolate variables
    return this._interpolate(selected.text, vars)
  }

  /**
   * Select a template for an event type (P1/P2).
   *
   * @param {string} eventType - Event type (e.g. "Corner", "But")
   * @param {object} vars - Variables for interpolation
   * @returns {string|null} Interpolated text, or null if no template found
   */
  selectForEvent(eventType, vars = {}) {
    if (!this.loaded) {
      this.onLog('[TEMPLATE-SELECTOR] Not loaded yet — skipping')
      return null
    }

    // 1. Get candidate templates for this event type
    let pool = getTemplatesForEvent(eventType)

    // 2. No template → return null (caller falls back to LLM)
    if (pool.length === 0) {
      return null
    }

    // 2b. If prevPlayer is null, prefer templates without {prevPlayer}
    if (!vars.prevPlayer && pool.length > 0) {
      const noPrev = pool.filter(t => !t.text.includes('{prevPlayer}'))
      if (noPrev.length > 0) pool = noPrev
    }

    // 3. Exclude templates in cooldown
    const cooldownSet = new Set(this.history)
    let filtered = pool.filter(t => !cooldownSet.has(t.id))

    // 4. Exclude last template played
    if (this.lastTemplateId) {
      filtered = filtered.filter(t => t.id !== this.lastTemplateId)
    }

    // Relax if all filtered out
    if (filtered.length === 0) {
      filtered = pool.filter(t => t.id !== this.lastTemplateId)
    }
    if (filtered.length === 0) {
      filtered = pool
    }

    // 5. Situation tags — boost matching templates
    const situationTags = this._getSituationTags(vars.team)
    if (situationTags.length > 0) {
      const tagged = filtered.filter(t =>
        t.tags && t.tags.some(tag => situationTags.includes(tag))
      )
      if (tagged.length > 0) {
        filtered = [...filtered, ...tagged]
      }
    }

    // 6. Weighted random selection
    const selected = this._weightedRandom(filtered)

    // 7. Record in history
    this._recordUsage(selected.id, selected.cooldown)

    // 8. Interpolate variables
    return this._interpolate(selected.text, vars)
  }

  /**
   * Weighted random selection from a pool of templates.
   */
  _weightedRandom(pool) {
    const totalWeight = pool.reduce((sum, t) => sum + (t.weight || 1), 0)
    let r = Math.random() * totalWeight
    for (const t of pool) {
      r -= (t.weight || 1)
      if (r <= 0) return t
    }
    return pool[pool.length - 1]
  }

  /**
   * Record template usage for cooldown tracking.
   */
  _recordUsage(templateId, cooldown) {
    this.lastTemplateId = templateId

    // Add to history (for cooldown)
    this.history.push(templateId)

    // Trim history to HISTORY_SIZE
    if (this.history.length > HISTORY_SIZE) {
      this.history = this.history.slice(-HISTORY_SIZE)
    }
  }

  /**
   * Compute situation tags based on current match state.
   */
  _getSituationTags(teamInPossession) {
    const tags = []

    // Score situation
    const score = state.lastScore
    if (score) {
      const parts = score.split('-').map(Number)
      if (parts.length === 2) {
        const [home, away] = parts
        if (home === away) {
          tags.push('TIED')
        } else {
          // Determine if the team in possession is leading or trailing
          const possessionIsHome = teamInPossession === 'HOME' ||
            teamInPossession === state.staticDetail?.homeTeam?.name ||
            teamInPossession === state.staticDetail?.homeTeam?.short_name
          const teamLeading = possessionIsHome ? (home > away) : (away > home)
          tags.push(teamLeading ? 'LEADING' : 'TRAILING')
        }
      }
    }

    // Minute situation
    const chrono = getChronoData()
    if (chrono.minute > 0) {
      if (chrono.minute <= 15) tags.push('EARLY')
      if (chrono.minute >= 80) tags.push('LATE')
    }

    return tags
  }

  /**
   * Interpolate template variables.
   * Reuses the same variable names as catalog-tts.js resolveVariables().
   */
  _interpolate(text, vars) {
    const player = vars.player || this._getPlayerName() || ''
    const prevPlayer = vars.prevPlayer || ''

    const replacements = {
      player,
      prevPlayer,
      team: vars.team || this._getTeamName('home') || '',
      opponent: vars.opponent || this._getTeamName('away') || '',
      side: vars.side || '',
      zone: vars.zone || '',
      minute: vars.minute || '',
      score: vars.score || state.lastScore || '0-0',
      goalkeeper: vars.goalkeeper || '',
      lostBy: vars.lostBy || '',
    }

    let result = text
    for (const [key, val] of Object.entries(replacements)) {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), val)
    }
    return result
  }

  /**
   * Get current player name from state.
   */
  _getPlayerName() {
    const info = state.lastPlayerInfo
    if (!info?.playerName) return null

    if (state.playerMapping && info.playerId) {
      const mapped = state.playerMapping[info.playerId]
      if (mapped) {
        const full = mapped.shortName || mapped.name || info.playerName
        // Extract surname: "K. Mbappe" -> "Mbappe"
        return full.replace(/^([A-Za-z\u00C0-\u024F]\.\s*)+/, '').trim() || full
      }
    }
    return info.playerName
  }

  /**
   * Get team short name from staticDetail.
   */
  _getTeamName(side) {
    const sd = state.staticDetail
    if (!sd) return null
    const team = side === 'home' ? sd.homeTeam : sd.awayTeam
    return team?.short_name || team?.name || null
  }

  /**
   * Reset anti-repetition state.
   */
  reset() {
    this.history = []
    this.lastTemplateId = null
  }
}
